import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { UserRole, type UserWithProfile } from '@klaro7/shared-types';

import { tokenManager } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import * as authService from '../services/auth.service';

/**
 * Returns the default redirect path after login based on user role.
 * Regular users stay on the platform directory; admin/professional go to dashboard.
 */
export function getDefaultRedirect(role: UserRole): string {
  if (role === UserRole.USER) return '/';
  return '/dashboard';
}

interface AuthState {
  user: UserWithProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<UserWithProfile>;
  register: (data: authService.RegisterPayload) => Promise<UserWithProfile>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  showLoginModal: boolean;
  loginRedirectTo: string | null;
  loginModalContext: 'support' | null;
  openLoginModal: (redirectTo?: string, context?: 'support') => void;
  closeLoginModal: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginRedirectTo, setLoginRedirectTo] = useState<string | null>(null);
  const [loginModalContext, setLoginModalContext] = useState<'support' | null>(null);

  // Check auth on mount.
  //
  // Un fallimento di getCurrentUser NON implica sessione scaduta: durante un
  // deploy l'API risponde 502 per qualche secondo, su mobile la rete va e
  // viene, e il rate limiter può rispondere 429. Buttare i token qui (come si
  // faceva) trasformava ogni errore transitorio in un logout forzato — era la
  // causa principale delle "sessioni che scadono" lamentate dai super admin.
  // Ora: retry con backoff sui fallimenti transitori; i token si buttano solo
  // quando è l'interceptor a rimuoverli (refresh rifiutato dal server).
  useEffect(() => {
    let cancelled = false;
    const RETRY_DELAYS_MS = [2000, 5000, 10000];

    const checkAuth = async () => {
      if (!tokenManager.getAccessToken()) {
        setState({ user: null, isLoading: false, isAuthenticated: false });
        return;
      }

      for (let attempt = 0; ; attempt++) {
        try {
          const user = await authService.getCurrentUser();
          if (!cancelled) setState({ user, isLoading: false, isAuthenticated: true });
          return;
        } catch {
          if (cancelled) return;

          // L'interceptor ha già rimosso i token: il refresh token è stato
          // rifiutato dal server, la sessione è davvero finita.
          if (!tokenManager.getAccessToken()) {
            setState({ user: null, isLoading: false, isAuthenticated: false });
            return;
          }

          const delay = RETRY_DELAYS_MS[attempt];
          if (delay === undefined) {
            // Errore transitorio persistente (API giù): arrenditi per questo
            // mount ma CONSERVA i token — al prossimo caricamento la sessione
            // riparte senza dover rifare login.
            setState({ user: null, isLoading: false, isAuthenticated: false });
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
          if (cancelled) return;
        }
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string): Promise<UserWithProfile> => {
    const { user, tokens } = await authService.login(email, password);
    tokenManager.setTokens(tokens);
    queryClient.clear();
    setState({ user, isLoading: false, isAuthenticated: true });
    return user;
  };

  const register = async (data: authService.RegisterPayload): Promise<UserWithProfile> => {
    const { user, tokens } = await authService.register(data);
    tokenManager.setTokens(tokens);
    queryClient.clear();
    setState({ user, isLoading: false, isAuthenticated: true });
    return user;
  };

  const logout = async () => {
    const refreshToken = tokenManager.getRefreshToken();
    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch {
        // Ignore logout errors
      }
    }
    tokenManager.clearTokens();
    queryClient.clear();
    setState({ user: null, isLoading: false, isAuthenticated: false });
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const user = await authService.getCurrentUser();
      setState((prev) => ({ ...prev, user }));
    } catch {
      // refresh failure is non-fatal; existing user stays in state
    }
  };

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!state.user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(state.user.role);
  };

  const openLoginModal = (redirectTo?: string, context?: 'support') => {
    setLoginRedirectTo(redirectTo ?? null);
    setLoginModalContext(context ?? null);
    setShowLoginModal(true);
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
    setLoginRedirectTo(null);
    setLoginModalContext(null);
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, refreshUser, hasRole, showLoginModal, loginRedirectTo, loginModalContext, openLoginModal, closeLoginModal }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
